<template>
  <div class="login-history">
    <ifield-b-modal
      id="login-history-modal"
      ref="modal"
      centered
      scrollable
      no-body-border
      size="lg"
      ok-only
      ok-variant="success"
      content-class="login-history-modal-content"
      body-class="login-history-modal-body"
      v-bind:title="$t('theLoginHistory.loginHistory')"
      v-bind:ok-title="$t('theLoginHistory.action.confirm')"
    >
      <p class="caption">
        {{ caption }}
      </p>
      <table class="history-table">
        <thead class="header">
          <tr>
            <th class="cell" v-t="'theLoginHistory.title.loginDate'" />
            <th class="cell" v-t="'theLoginHistory.title.product'" />
            <th class="cell">OS</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="history in historiesModified"
            v-bind:key="history.id"
            class="table-row"
          >
            <td class="cell">{{ history.loginAt }}</td>
            <td class="cell">{{ history.product }}</td>
            <td class="cell">{{ history.os }}</td>
          </tr>
        </tbody>
      </table>
    </ifield-b-modal>
    <loading-mask is-full-screen v-bind:is-loading="isLoading" />
  </div>
</template>

<script>
import moment from 'moment'
import { RestApiClient } from 'ifield-core-components'
import i18n from '@/i18n'
import { useHandleApiError } from '@/composables/UseHandleApiError'
import { toLocalTz } from '@/js/TimezoneHelper'
import { DATE_FORMAT_SLASH, TIME_FORMAT_SECOND } from '@/constants'

// 表示用日時フォーマット
const DATETIME_FORMAT = `${DATE_FORMAT_SLASH} ${TIME_FORMAT_SECOND}`

// 製品表示テキスト定義
const PRODUCT_TEXT_MAP = new Map([
  ['users_web', i18n.t('theLoginHistory.product.usersWeb')],
  ['admin_web', i18n.t('theLoginHistory.product.adminWeb')],
  ['dashboard', i18n.t('theLoginHistory.product.dashboard')],
  ['smartphone_app', i18n.t('theLoginHistory.product.application')],
  ['operation_tools', 'iField Operation Tools'],
])

export default {
  name: 'TheLoginHistory',
  props: {
    /**
     * 表示件数
     */
    count: {
      type: Number,
      default: 10,
    },
  },
  setup() {
    const { handleApiError } = useHandleApiError()
    return { handleApiError }
  },
  data: function () {
    return {
      // 履歴データ
      histories: [],
      // ローディングフラグ
      isLoading: false,
    }
  },
  computed: {
    /**
     * トップに表示する説明文
     * @type {String}
     */
    caption: function () {
      return this.$t('theLoginHistory.message.shownLoginHistories', {
        n: this.count,
      })
    },
    /**
     * 表示用に加工した履歴データ
     * @type {Array.<Object>}
     */
    historiesModified: function () {
      return this.histories.map((d) => ({
        id: d.id,
        loginAt: moment(d.login_at).format(DATETIME_FORMAT),
        product: PRODUCT_TEXT_MAP.get(d.client_type) || '-',
        os: d.os,
      }))
    },
  },
  methods: {
    /**
     * モーダル表示
     * @public
     */
    showModal: async function () {
      this.$_showLoading()

      try {
        await this.$_getLoginHistories()
      } catch {
        return
      } finally {
        this.$_hideLoading()
      }

      this.$refs['modal'].show()
    },

    /**
     * ログイン履歴を取得する
     */
    $_getLoginHistories: async function () {
      const url = RestApiClient.createApiUrl('login_history')
      url.searchParams.append('limit', this.count)

      try {
        const json = await RestApiClient.executeGetApi(url)
        this.histories = json.data.map((d) => ({
          ...d,
          login_at: toLocalTz(d.login_at),
        }))
      } catch (error) {
        this.handleApiError(error)
        throw error
      }
    },

    /**
     * ローディングを表示する
     */
    $_showLoading: function () {
      this.isLoading = true
    },

    /**
     * ローディングを非表示する
     */
    $_hideLoading: function () {
      this.isLoading = false
    },
  },
}
</script>

<style lang="scss" scoped>
::v-deep .login-history-modal-content {
  width: fit-content;
  max-width: 640px;
}

::v-deep .login-history-modal-body {
  width: min-content;
  padding: 24px 32px 32px;
  // 上部 padding のみ caption 部の line-height で生まれるスペース分を差し引く
  padding-top: calc(24px - #{$font-size-middle} * (1.7 - 1) / 2);
  font-size: $font-size-middle;
}

.caption {
  // line-height で生まれるスペース分を差し引く
  margin-bottom: calc(1rem - #{$font-size-middle} * (1.7 - 1) / 2);
}

$-cell-height: calc(#{$font-size-middle} + 18px); // 文字サイズ + 行間18px
$-header-cell-height: calc(
  #{$font-size-middle} + 2 * 16px
); // 文字サイズ + 上下 padding 16px

.history-table {
  width: auto;

  .cell {
    height: $-cell-height;
    line-height: 1;
    vertical-align: bottom;
    white-space: nowrap;
    padding: 0 28px;
  }

  .header {
    border-bottom: 1px solid $border-color;

    .cell {
      height: $-header-cell-height;
      vertical-align: middle;
    }
  }

  .table-row {
    &:first-child {
      .cell {
        // 先頭行だけ行間をheaderの上下paddingと合わせる
        height: calc(#{$font-size-middle} + 16px);
      }
    }
  }
}
</style>
